import React, { useCallback, useState } from 'react';

import { useMarketTickerQuery } from 'api/queries/tickersQueries';
import { ApiOrder, ApiPosition } from 'api/types';
import { getBestPriceFromDisplayedOrderbook } from 'components/orders/orderBook/orderBookService';
import {
  checkOrderCrossingSpread,
  checkStopOrderInstantExecutionError,
  determineStopType,
  middleOrderbookPrice,
} from 'components/orders/orderUtil';
import { cancelAndRecreateOrder } from 'components/orders/repriceOrderUtil';
import { calculateTPSLPriceChangePercent } from 'components/positions/takeProfitStopLoss/tpSlUtils';
import { useIsMobile } from 'hooks/useIsMobile';
import { makeEthersErrorHumanRedable } from 'lib/ethereum/ethersErrors';
import { displayGlobalError } from 'reduxState/exchange/errorModalReducer';
import { staticDispatch } from 'reduxState/store';
import { useSelector } from 'reduxState/storeCore';

import OrderLinesRiskyUpdateModal from './OrderLinesRiskyUpdateModal';
import { resetOrderPrice } from './ordersLines';

/**
 * Desktop Only price update.
 * Chart handling of updating orde price,
 * with basic error handling and confirmation on possible instant execution
 */
export function useUpdateOrderPrice({
  currentMarket,
  currentMarketPosition,
}: {
  currentMarket: string | undefined;
  currentMarketPosition: ApiPosition | undefined | false;
}) {
  const isMobile = useIsMobile();
  const wallet = useSelector(state => state.wallet);
  const delegatedKeyAuthorization = useSelector(
    state => state.delegatedKey.authorization
  );
  const { data: marketTicker } = useMarketTickerQuery({
    market: isMobile ? currentMarket : undefined,
    websocketSubscribe: false,
  });

  const [riskyOrderUpdate, setRiskyOrderUpdate] = useState<
    | {
        order: ApiOrder;
        newPrice: number;
      }
    | undefined
  >();

  const handleUpdateOrderPrice = useCallback(
    (order: ApiOrder, updatedPrice: number, riskConfirmed = false) => {
      if (isMobile) {
        return;
      }
      console.log('[orderLines] Updating order price');
      const stopType = determineStopType(order.type);
      const lastPrice = marketTicker ? marketTicker?.close : false;
      const bestAsk = getBestPriceFromDisplayedOrderbook('ask');
      const bestBid = getBestPriceFromDisplayedOrderbook('bid');
      const midOrLastPrice =
        bestAsk && bestBid ? middleOrderbookPrice(bestAsk, bestBid) : lastPrice; // Fallback to last price if not available

      if (!midOrLastPrice) {
        console.warn('[orderLines] Missing orderbook/lastPrice');
        return;
      }
      if (!riskConfirmed) {
        let crossSpreadError: boolean;
        if (stopType) {
          if (!currentMarketPosition || !order.triggerType) {
            staticDispatch(
              displayGlobalError('Unknown position or triggerType')
            );
            return;
          }
          const currentPrice =
            order.triggerType === 'index'
              ? currentMarketPosition.indexPrice
              : midOrLastPrice;
          crossSpreadError = checkStopOrderInstantExecutionError({
            currentPrice,
            positionIsShort: Number(currentMarketPosition.quantity) < 0,
            price: updatedPrice,
            type: stopType,
          });
        } else {
          crossSpreadError = checkOrderCrossingSpread({
            midOrLastPrice,
            price: updatedPrice,
            side: order.side,
          });
        }
        if (crossSpreadError) {
          setRiskyOrderUpdate({
            order,
            newPrice: updatedPrice,
          });
          return;
        }
      }
      const displayTPSLWarning = (tpslPrice: string, type: 'TP' | 'SL') => {
        staticDispatch(
          displayGlobalError(
            `Unable to place order. Order price ${
              Number(updatedPrice) > Number(tpslPrice) ? 'greater' : 'smaller'
            } than ${type} price.`,
            'warning',
            undefined
          )
        );
      };
      const {
        conditionalTakeProfitOrder: conditionalTakeProfit,
        conditionalStopLossOrder: conditionalStopLoss,
      } = order;
      if (
        conditionalTakeProfit &&
        conditionalTakeProfit.triggerPrice &&
        (calculateTPSLPriceChangePercent({
          entryPrice: updatedPrice,
          targetAction: conditionalTakeProfit?.side,
          targetPrice: conditionalTakeProfit.triggerPrice,
          trigger: 'takeProfit',
          triggerBy: 'byChange',
          initialMarginFraction: undefined,
        }) || 0) < 0
      ) {
        displayTPSLWarning(conditionalTakeProfit.triggerPrice, 'TP');
        resetOrderPrice(order);
        return;
      }
      if (
        conditionalStopLoss &&
        conditionalStopLoss.triggerPrice &&
        (calculateTPSLPriceChangePercent({
          entryPrice: updatedPrice,
          targetAction: conditionalStopLoss?.side,
          targetPrice: conditionalStopLoss.triggerPrice,
          trigger: 'stopLoss',
          triggerBy: 'byChange',
          initialMarginFraction: undefined,
        }) || 0) < 0
      ) {
        displayTPSLWarning(conditionalStopLoss.triggerPrice, 'SL');
        resetOrderPrice(order);
        return;
      }

      cancelAndRecreateOrder({
        order,
        updatedPrice,
        wallet,
        delegatedKeyAuthorization,
      }).catch(error => {
        console.warn('Could not reprice order - ignoring');
        console.warn(error);
        staticDispatch(displayGlobalError(makeEthersErrorHumanRedable(error)));
      });
    },
    [
      currentMarketPosition,
      delegatedKeyAuthorization,
      isMobile,
      marketTicker,
      wallet,
    ]
  );

  return {
    handleUpdateOrderPrice,
    confirmationOrderUpdatePlaceholder: riskyOrderUpdate ? (
      <OrderLinesRiskyUpdateModal
        newPrice={riskyOrderUpdate.newPrice}
        onClose={() => {
          setRiskyOrderUpdate(undefined);
          resetOrderPrice(riskyOrderUpdate.order);
        }}
        onConfirm={() => {
          handleUpdateOrderPrice(
            riskyOrderUpdate.order,
            riskyOrderUpdate.newPrice,
            true
          );
          setRiskyOrderUpdate(undefined);
        }}
      />
    ) : null,
  };
}
